import springyModel from '../../tools/SpringyModel.js';
import springyRegex from '../../tools/springyRegex.js';
import { ChatAnswer } from './ChatQuestion.js';

/**
  * Patron session object.
  *
  * Defaults for patrons
  * @prop {String} sessionId Unique session id.
  * @prop {String} name Name field.
  * @prop {String} client_contact Contact info (email/phone).
  * @prop {String} local_id patron's local id... like a library card number
  * @prop {String} role always "user"
  * @prop {String} user_agent User agent string.
  * @prop {Boolean} isMobile Are they on a mobile browser (we think).
  * @prop {String} ip Ip address.
  * @prop {String} referer Referer url.
  * @prop {String} client_question Initial question from patron.
  * @prop {Number} did Requested chat department id.
  * @prop {Number} uid Requested chat user uid. 0 means they talk to a department. (the users they talk to are in ops array)
  * @prop {String} coopId Coop id chat fell back to in lieu of uid/did.
  * @prop {String} widget Name of widget.
  * @prop {Object} user1 Custom field name/value.
  * @prop {Object} user2 Custom field name/value.
  * @prop {Object} user3 Custom field name/value.
  * @prop {Array} consortium Array of consortial ids.
  * @prop {String} peel Session id they just peeled out from.
  * @prop {Number} iid Site IID.
  * @prop {String} joined When the op logged in (ISO 8601 format).
  * @prop {Object} transferTarget If patron is being transferred this is the deptId, opId, instId, coopIds of target
  * @prop {Array} transferHist Info about who they talked to previous to a transfer.
  * @prop {Boolean} isPending Is patron in pending status
  * @prop {Boolean} isConnected Is patron still online (so object can stick around after disconnect)
  * @prop {Array} ops Array of opIds for ops in the conversation
  * @prop {Boolean} isAnswered Has anyone picked up the chat yet
  * @prop {Array<ChatMessage>} messages Array of ChatMessage objects for view
  * @prop {Boolean} isTyping Is the patron typing.
  * @prop {Boolean} isSelected On the op side is this the currently viewed patron.
  * @prop {Number} newMessages Messages that have occurred while op was not viewing chat.
  * @prop {String} disconnectReason Vague message about why they patron disconnected.
  * @prop {Boolean} popped Is this chat popped out in the UI.
  * @prop {String} region Region code for op's region.
  * @prop {Object} site Original site that this chat came in on.
  * @prop {Number} site.iid Original site inst id.
  * @prop {String} site.name Original site name.
  * @prop {Number} site.unit_id Shared Group unit id in site if applicable.
  * @prop {String} site.unit_name Shared Group unit name in site if applicable.
  * @prop {Array} site.consortium Ids of consortia original site belongs to.
  * @prop {Array} fallbacks Array of op/dept/coop fallbacks.
  * @prop {String} flowUuid If came from Bot what flow was it.
  * @prop {Array<ChatAnswer>} answers Question answers from bot/bank.
  * @prop {Number} ticketId Ticket id if one was created.
  */
const patronSession = Object.create(springyModel);
patronSession.defaults = {
    sessionId: '',
    name: '',
    client_contact: '',
    local_id: '',
    role: 'user',
    user_agent: '',
    isMobile: false,
    ip: '',
    referer: '',
    client_question: '',
    did: -1,
    uid: 0,
    coopId: '',
    widget: '',
    user1: '',
    user2: '',
    user3: '',
    consortium: '',
    peel: '',
    iid: 0,
    joined: '',
    transferTarget: null,
    transferHist: [],
    isPending: false,
    isConnected: false,
    isAnswered: false,
    ops: [],
    messages: [],
    isTyping: false,
    isSelected: false,
    newMessages: 0,
    disconnectReason: '',
    popped: false,
    region: '',
    site: {},
    fallbacks: [],
    flowUuid: '',
    answers: []
};
/**
 * @prop {Number} ticketId For now storing bot ticket id
 * will add to defaults if we need to send it to chat server or something...
 */
patronSession.ticketId = 0;
/**
  * @prop {Boolean} So we can have internal and patron chats in the same method
  * and know which is which. Easier if we switched to classes so one could check the class.
  */
patronSession.isInternal = false;
/**
  * @prop {Number|null} pendingTimeout Timeout function id for assuming a pending op is staying pending.
  */
patronSession.pendingTimeout = null;
/**
  * @prop {Boolean} Flag for if the message consolidation process was triggered yet.
  */
patronSession.msgConsolidated = false;
/**
 * @prop {Array<Object>|null} internalChatMsgs op2op messages
 * if this were an array it would pass by reference so all chats would
 * get the same set of internal messages.
 */
patronSession.internalChatMsgs = null;
/**
  * Get the channel/room for this patron's chats.
  * @return {String}
  */
patronSession.getChannel = function() {
    return this.getRoom();
};
/**
  * Get the channel/room for this patron's chats.
  * @return {String}
  */
patronSession.getRoom = function() {
    const sessionId = this.get('sessionId');
    return sessionId !== '' ? `chat-${this.get('sessionId')}` : '';
};
/**
  * Set contact info and make sure it matches expected formats.
  * @param {String} text Contact info from patron or op.
  */
patronSession.setContactInfo = function(text) {
    let temp = text.trim();
    // Allow resetting here.
    if (temp === '') {
        this.set('client_contact', '');
        return;
    }
    // Is it an email, then OK.
    if (temp.match(springyRegex.email) !== null) {
        this.set('client_contact', temp);
        return;
    }
    // See if it could be a phone number.
    // Reformat as best we can to just digits.
    temp = temp.replace(/[-()\s.]/g, '');
    if (temp.match(springyRegex.phone) !== null) {
        this.set('client_contact', temp);
    }
    // Throw?
};
/**
 * Check if the saved contact info is an SMS number
 * @returns {Bool}
 */
patronSession.isContactSMS = function() {
    return this.get('client_contact').match(springyRegex.phone) !== null;
};
/**
  * Convert the disconnect reason into something useful.
  * @param {String} reason Reason via socket.io (cryptic).
  */
patronSession.setDisconnectReason = function(reason) {
    // Ignoring most of them at this point.
    switch (reason.toLowerCase()) {
        case 'transport close':
            this.set('disconnectReason', 'Closed or left page.');
    }
};
/**
  * Add a new message to the array
  * @param {ChatMessage} message Message data
  */
patronSession.addMessage = function(message) {
    const messages = this.get('messages');
    messages.push(message);
    const updates = {
        messages: messages
    };
    if (!this.get('isSelected')) {
        let newMessages = this.get('newMessages');
        newMessages++;
        updates.newMessages = newMessages;
    }
    this.set(updates);
    this.trigger('chat:message', message);
};
/**
  * Rvemo a message from the array
  * @param {Number} msgId Message id
  */
patronSession.removeMessage = function(msgId) {
    const messages = this.get('messages');
    const index = messages.findIndex((msg) => {
        return msg.msgId === msgId;
    });
    if (index === -1) {
        return;
    }
    messages.splice(index, 1);
    this.trigger('chat:removemsg', msgId);
};
/**
  * Add a new internal message to the array
  * @param {ChatMessage} message Message data
  */
patronSession.addInternalMessage = function(message) {
    if (!this.internalChatMsgs) {
        // create new array not attached to prototype parent
        this.internalChatMsgs = [];
    }
    this.internalChatMsgs.push(message);
};
/**
  * Reset some fields that are specific to the local context (of the op dashboard or widget)
  * and shouldn't be passed around.
  * Don't trigger change events!
  */
patronSession.resetContext = function() {
    this.set({
        newMessages: 0,
        isSelected: false,
        isTyping: false
    }, { silent: true });
};
/**
  * Update some patron data.
  * For when we have patron data but cascade sends us the patron again for some reason.
  * This just changes fields that are possible/likely to change after the patron was created.
  * @param {Object} patronData Data object from cascade.
  */
patronSession.updateFromServer = function(patronData) {
    const props = {
        transferHist: patronData.transferHist,
        transferTarget: patronData.transferTarget,
        isAnswered: patronData.isAnswered,
        did: patronData.did,
        uid: patronData.uid,
        iid: patronData.iid,
        coopId: patronData.coopId || '',
        isConnected: true
    };
    if (patronData.ops && Array.isArray(patronData.ops)) {
        props.ops = patronData.ops;
    }
    if (patronData.messages.length > 0) {
        const currentMessages = this.get('messages');
        if (currentMessages.length === 0) {
            // trigger event for each message
            // in case op already looked at this patron
            patronData.messages.forEach((message) => {
                currentMessages.push(message);
                this.trigger('chat:message', message);
            });
        } else {
            // Add new messages based on timestamp
            const latestMessage = currentMessages[currentMessages.length - 1];
            if (!latestMessage.ts) {
                props.messages = patronData.messages;
            } else {
                const latestTs = new Date(latestMessage.ts);
                patronData.messages.forEach(function(message) {
                    const ts = new Date(message.ts);
                    if (latestTs >= ts) {
                        return;
                    }
                    currentMessages.push(message);
                    this.trigger('chat:message', message);
                }, this);
            }
        }
    }
    this.set(props);

    if (patronData.addOp) {
        // at this point all added ops are claims...
        this.addOp(patronData.addOp, true);
    }

    // reset this in case the op disconnected and then reconnected and the patron came back
    this.msgConsolidated = false;
};
/**
  * Set a timout for pending users to trigger an event.
  * Generally, this would be triggering a disconnected type change.
  * @param {Function} callback Callback to execute.
  */
patronSession.setPendingTimeout = function(callback) {
    window.clearTimeout(this.pendingTimeout);
    this.pendingTimeout = window.setTimeout(callback, 60000);
};
/**
  * Clear the pending timeout if user reconnects.
  */
patronSession.clearPendingTimeout = function() {
    window.clearTimeout(this.pendingTimeout);
};
/**
  * Is an op chatting with this patron.
  * @param {Number} opId LA User id of operator.
  * @returns {Boolean}
  */
patronSession.isOpChatting = function(opId) {
    return (this.ops.indexOf(opId) !== -1);
};
/**
  * Add op to the ops array, avoid duplicates.
  * @param {Number} opId Operator's id.
  * @param {Boolean} isOwner Is this the new owner, if so put them first.
  */
patronSession.addOp = function(opId, isOwner = false) {
    const index = this.ops.indexOf(opId);
    if (index !== -1 && !isOwner) {
        return;
    }
    // Make sure we have a fresh array for the first op
    // else we might have issues pushing to the prototype's prop.
    if (this.ops.length === 0) {
        this.ops = [];
    }
    if (!isOwner) {
        this.ops.push(opId);
    } else {
        if (index !== -1) {
            this.ops.splice(index, 1);
        }
        this.ops.unshift(opId);
    }
    this.trigger('change:ops');
    this.trigger('change');
};
/**
  * Remove an op from the ops array.
  * @param {Number} opId LA user id of operator.
  * @returns {Boolean}
  */
patronSession.removeOp = function(opId) {
    const index = this.ops.indexOf(opId);
    if (index === -1) {
        return false;
    }
    this.ops.splice(index, 1);
    this.trigger('change:ops');
    this.trigger('change');
    return true;
};
/**
  * Save answer to chatbot question
  * @param {ChatAnswer} answer a
  */
patronSession.addAnswer = function(answer) {
    if (!(answer instanceof ChatAnswer)) {
        return;
    }
    const answers = this.get('answers');
    answers.push(answer);
    this.set('answers', answers);
};
/**
  * Get answers by questionId.
  * @param {Number} questionId one to return
  * @returns {ChatAnswer|undefined}
  */
patronSession.getAnswerByQuestionId = function(questionId) {
    return this.get('answers').find((answer) => {
        return answer.questionId === questionId;
    });
};

/**
 * Remove coop from fallback list
 * @param {String} coopId Coop id
 */
patronSession.removeFallbackByCoopId = function(coopId) {
    const index = this.get('fallbacks').findIndex((rule) => {
        return rule.c && rule.c === coopId;
    });
    if (index === -1) {
        return;
    }
    this.get('fallbacks').splice(index, 1);
};

export default patronSession;
